import React, { useState, useEffect } from 'react';

// Compact relative-ish timestamp for the queue "When" column (e.g. "2h ago",
// "Mar 3"). Full date/time shown on hover via fmtWhenFull.
const fmtWhen = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d)) return '—';
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  // Older than a week: show date AND time (e.g. "Jul 3, 2:45 PM").
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};
const fmtWhenFull = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return isNaN(d) ? '' : d.toLocaleString();
};

// Match-tier from score → label + accent, mirroring JobRight's STRONG/GOOD/FAIR.
//
// `color` is a CSS variable rather than a hex so the light theme can darken
// the whole ramp in one place (see --ajp-tier-* in AutoJobApply.css); `key`
// exists because the colour is no longer a usable SVG gradient id.
const matchTier = (score) => {
  const s = Number(score) || 0;
  if (s >= 85) return { key: 'strong', label: 'STRONG MATCH', color: 'var(--ajp-tier-strong)' };
  if (s >= 70) return { key: 'good',   label: 'GOOD MATCH',   color: 'var(--ajp-tier-good)' };
  if (s >= 50) return { key: 'fair',   label: 'FAIR MATCH',   color: 'var(--ajp-tier-fair)' };
  return { key: 'low', label: 'LOW MATCH', color: 'var(--ajp-tier-low)' };
};

// JobRight shows seniority / years / workplace / "early applicant" — we don't
// store those explicitly, so derive them from the title, location, and post age.
// Six levels, matching JobRight's Experience-Level filter. Order matters —
// check the most senior signals first so "Senior Staff" → Lead/Staff, etc.
const SENIORITY_LEVELS = ['Intern/New Grad', 'Entry Level', 'Mid Level', 'Senior Level', 'Lead/Staff', 'Director/Executive'];
const seniorityOf = (title = '') => {
  const t = title.toLowerCase();
  if (/\b(chief|vp|vice president|head of|director|executive|president|c[te]o|cfo)\b/.test(t)) return 'Director/Executive';
  if (/\b(staff|principal|lead|distinguished|architect|fellow)\b/.test(t)) return 'Lead/Staff';
  if (/\b(senior|sr\.?)\b/.test(t)) return 'Senior Level';
  if (/\b(intern|internship|new grad|graduate|apprentice|co-?op)\b/.test(t)) return 'Intern/New Grad';
  if (/\b(junior|jr\.?|entry|associate)\b/.test(t)) return 'Entry Level';
  return 'Mid Level';
};
// Approximate years-of-experience a level implies (for the slider filter).
const yearsNum = {
  'Intern/New Grad': 0, 'Entry Level': 1, 'Mid Level': 3,
  'Senior Level': 5, 'Lead/Staff': 8, 'Director/Executive': 11,
};
const yearsOf = (sen) => ({
  'Director/Executive': '10+ years exp', 'Lead/Staff': '8+ years exp',
  'Senior Level': '5+ years exp', 'Mid Level': '3+ years exp',
  'Entry Level': '1+ years exp', 'Intern/New Grad': '0-1 years exp',
}[sen] || '3+ years exp');

// Job type, derived from the title (we don't store it explicitly).
const JOB_TYPES = ['Full-time', 'Contract', 'Part-time', 'Internship'];
const jobTypeOf = (title = '') => {
  const t = title.toLowerCase();
  if (/\b(intern|internship)\b/.test(t)) return 'Internship';
  if (/\b(contract|contractor|c2c|temp|temporary|1099)\b/.test(t)) return 'Contract';
  if (/\bpart[\s-]?time\b/.test(t)) return 'Part-time';
  return 'Full-time';
};
const workplaceOfLoc = (loc = '') => {
  const s = String(loc).toLowerCase();
  if (/\bremote\b/.test(s)) return 'Remote';
  if (/\bhybrid\b/.test(s)) return 'Hybrid';
  return 'On-site';
};
const isEarly = (ts) => ts && (Date.now() - new Date(ts).getTime()) < 48 * 3600e3;

// Circular % ring — SVG so the arc is smooth with rounded caps (the old
// conic-gradient had a hard seam + flat track that looked crude).
function MatchRing({ score, color, tierKey = 'low', size = 66 }) {
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  const stroke = 6;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  // Keyed off the tier, not the colour — `color` is now a var() reference and
  // `url(#mr-var(--x))` would not parse.
  const gid = `mr-${tierKey}`;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        <defs>
          {/* stop-color goes through `style`: as a presentation attribute it
              would be handed the literal string "var(…)" and ignored. */}
          <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" style={{ stopColor: color }} stopOpacity="0.7" />
            <stop offset="100%" style={{ stopColor: color }} />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" style={{ stroke: 'var(--ajp-ring-track)' }} strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={`url(#${gid})`} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ * (1 - pct / 100)} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        <span style={{ fontSize: size * 0.3, fontWeight: 800, color: 'var(--ajp-ink)', lineHeight: 1, letterSpacing: '-0.02em' }}>{pct}</span>
        <span style={{ fontSize: size * 0.17, fontWeight: 700, color: 'var(--ajp-ink-dim)', alignSelf: 'flex-start', marginTop: size * 0.14 }}>%</span>
      </div>
    </div>
  );
}

const statusChip = (status) => ({
  padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: '0.02em',
  background: status === 'applied' ? 'var(--ajp-ok-wash)' :
              status === 'failed'   ? 'var(--ajp-danger-wash)' :
              status === 'rejected' ? 'var(--ajp-neutral-wash)' :
              status === 'approved' ? 'var(--ajp-warn-wash)' :
                                      'var(--ajp-accent-wash)',
  color:      status === 'applied' ? 'var(--ajp-ok)' :
              status === 'failed'   ? 'var(--ajp-danger)' :
              status === 'rejected' ? 'var(--ajp-neutral)' :
              status === 'approved' ? 'var(--ajp-warn)' : 'var(--ajp-accent)',
  textTransform: 'capitalize',
});

// Tiny line icons (inline SVG so no dependency / theme-matched via currentColor).
const Ic = {
  pin: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>,
  clock: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>,
  grid: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
  home: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9.5 12 3l9 6.5"/><path d="M5 10v10h14V10"/></svg>,
  bag: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  crown: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17 5 7l5 5 2-6 2 6 5-5 2 10z"/></svg>,
  cal: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M8 2v4M16 2v4M3 10h18"/></svg>,
  dollar: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M14.5 9a2.5 2 0 0 0-2.5-1.5c-1.5 0-2.5.8-2.5 2s1 1.6 2.5 2 2.5.9 2.5 2-1 2-2.5 2A2.5 2 0 0 1 9.5 15M12 6v1.5M12 16.5V18"/></svg>,
};
const Meta = ({ icon, children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ajp-ink-dim)' }}>
    <span style={{ color: 'var(--ajp-ink-muted)', display: 'flex' }}>{icon}</span>{children}
  </span>
);

// Company logo — fetches the company's icon by domain (slug + .com) from
// DuckDuckGo's free icon service, and falls back to the initials avatar when
// there's no icon (or it fails to load). (Clearbit's logo API is discontinued.)
function CompanyLogo({ company, initials }) {
  const [failed, setFailed] = useState(false);
  const domain = String(company || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const box = { width: 40, height: 40, borderRadius: 10, flexShrink: 0 };
  if (failed || !domain) {
    return (
      <div style={{ ...box, background: 'linear-gradient(135deg,#7c3aed,#a855f7)', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14,
        boxShadow: '0 4px 14px -6px rgba(124,58,237,0.6)' }}>{initials}</div>
    );
  }
  return (
    <img src={`https://icons.duckduckgo.com/ip3/${domain}.com.ico`} alt={company} loading="lazy"
      onError={() => setFailed(true)}
      onLoad={(e) => { if (!e.target.naturalWidth) setFailed(true); }} // 0-byte/blank icon → fall back
      style={{ ...box, objectFit: 'contain', background: '#fff', padding: 5, border: '1px solid var(--ajp-line)' }} />
  );
}

// JobRight-style job card: left content + right match panel.
// Small round icon button (block / like), JobRight-style.
function RoundBtn({ title, active, activeColor, onClick, children }) {
  return (
    <button title={title} onClick={onClick}
      style={{
        width: 28, height: 28, borderRadius: '50%', flexShrink: 0, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: active ? `${activeColor}22` : 'var(--ajp-fill-2)',
        border: `1px solid ${active ? activeColor : 'var(--ajp-line)'}`,
        color: active ? activeColor : 'var(--ajp-ink-dim)', transition: 'all .15s',
      }}>
      {children}
    </button>
  );
}

function JobCard({ j, usage, decideJob, apiBaseUrl, liked, onToggleLike }) {
  const tier = matchTier(j.match_score);
  const initials = (j.company || '?').trim().slice(0, 2).toUpperCase();
  const posted = fmtWhen(j.discovered_at);
  const seniority = seniorityOf(j.title);
  const workplace = workplaceOfLoc(j.location);
  const [showReason, setShowReason] = useState(false);

  // "Apply with Autofill" — hand the job to the browser extension, which opens
  // the ATS page and fills the form in the user's own Chrome.
  const applyWithAutofill = () => {
    window.postMessage({
      terviewExt: true, type: 'EXT_AUTOFILL',
      payload: {
        token: localStorage.getItem('token'),
        apiBase: apiBaseUrl,
        job: {
          apply_url: j.apply_url, title: j.title, company: j.company,
          location: j.location, platform: j.platform, match_score: j.match_score,
        },
      },
    }, '*');
  };

  const pill = (bg, color, children) => (
    <span style={{ padding: '3px 10px', borderRadius: 8, fontSize: 11.5, fontWeight: 600, background: bg, color }}>
      {children}
    </span>
  );

  return (
    <div style={{
      display: 'flex', alignItems: 'stretch', overflow: 'hidden', textAlign: 'left',
      background: 'var(--ajp-fill)', border: '1px solid var(--ajp-line)',
      borderRadius: 18, marginBottom: 14,
    }}>
      {/* ── Left: content ── */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', padding: '13px 18px' }}>
        {/* top tag row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 9 }}>
          {posted && pill('var(--ajp-ok-wash)', 'var(--ajp-ok)', posted)}
          {isEarly(j.discovered_at) && pill('var(--ajp-fill-2)', 'var(--ajp-ink-dim)', '✦ Be an early applicant')}
          {j.status === 'applied' && pill('var(--ajp-ok-wash)', 'var(--ajp-ok)', '✓ Applied')}
        </div>

        {/* logo + title + company */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <CompanyLogo company={j.company} initials={initials} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <a href={j.apply_url} target="_blank" rel="noreferrer"
               style={{ color: 'var(--ajp-ink)', fontSize: 15, fontWeight: 700, textDecoration: 'none', letterSpacing: '-0.01em', lineHeight: 1.3, display: 'block' }}>
              {j.title}
            </a>
            <div style={{ fontSize: 13, marginTop: 2 }}>
              <span style={{ fontWeight: 600, color: 'var(--ajp-ink-2)' }}>{j.company}</span>
              <span style={{ color: 'var(--ajp-ink-muted)' }}>{'  /  '}{j.platform}</span>
            </div>
          </div>
        </div>

        {/* detail row with icons (JobRight layout) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px 18px', flexWrap: 'wrap', margin: '10px 0 0' }}>
          {j.location && <Meta icon={Ic.pin}>{j.location}</Meta>}
          <Meta icon={Ic.home}>{workplace}</Meta>
          <Meta icon={Ic.bag}>Full-time</Meta>
          <Meta icon={Ic.crown}>{seniority}</Meta>
          <Meta icon={Ic.cal}>{yearsOf(seniority)}</Meta>
          {j.salary && <Meta icon={Ic.dollar}><span style={{ color: 'var(--ajp-ok)', fontWeight: 600 }}>{j.salary}</span></Meta>}
        </div>

        {/* expandable "why this matches" reason */}
        {showReason && j.match_reason && (
          <p style={{ margin: '10px 0 0', fontSize: 12.5, lineHeight: 1.5, color: 'var(--ajp-accent)' }}>
            ✨ {j.match_reason}
          </p>
        )}

        {/* failure reason (if a job failed previously) */}
        {j.status === 'failed' && j.failure_reason && (
          <div title={j.failure_reason}
               style={{ marginTop: 10, fontSize: 12.5, color: 'var(--ajp-danger)', maxWidth: 620,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {j.failure_reason}
          </div>
        )}

        {/* actions row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--ajp-line-soft)', marginRight: -18, marginLeft: -18, paddingLeft: 18, paddingRight: 18 }}>
          <RoundBtn title="Not interested" onClick={() => decideJob(j.id, 'reject')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/><path d="M5.6 5.6l12.8 12.8"/></svg>
          </RoundBtn>
          <RoundBtn title={liked ? 'Liked' : 'Like'} active={liked} activeColor="#f472b6" onClick={onToggleLike}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill={liked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"/></svg>
          </RoundBtn>
          {j.match_reason && (
            <button onClick={() => setShowReason(v => !v)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 11px', borderRadius: 999,
                       fontSize: 12, fontWeight: 700, cursor: 'pointer',
                       background: showReason ? 'var(--ajp-accent-wash)' : 'var(--ajp-fill-2)',
                       color: showReason ? 'var(--ajp-accent)' : 'var(--ajp-ink-dim)',
                       border: `1px solid ${showReason ? 'var(--ajp-accent-line)' : 'var(--ajp-line)'}` }}>
              ✨ Why match
            </button>
          )}
          <button onClick={applyWithAutofill}
            title="Open the application and autofill it in your browser"
            style={{ marginLeft: 'auto', padding: '7px 16px', borderRadius: 999, fontSize: 12, fontWeight: 800,
                     letterSpacing: '0.02em', background: 'var(--ajp-ok-fill)', color: 'var(--ajp-ok-fill-ink)', border: 'none', cursor: 'pointer',
                     boxShadow: '0 6px 18px -8px rgba(74,222,128,0.7)' }}>
            APPLY WITH AUTOFILL
          </button>
        </div>
      </div>

      {/* ── Right: match panel (recessed, ring + tier + source) ── */}
      <div style={{
        width: 152, flexShrink: 0,
        // color-mix rather than the old `${tier.color}1f` hex-alpha suffix —
        // tier colours are var() references now, which can't be concatenated.
        background: `radial-gradient(115% 60% at 50% -8%, color-mix(in srgb, ${tier.color} 12%, transparent), transparent 60%), var(--ajp-match-bg)`,
        borderLeft: '1px solid var(--ajp-line-soft)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 11, padding: '16px 12px',
      }}>
        <MatchRing score={j.match_score} color={tier.color} tierKey={tier.key} size={68} />
        <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.09em', color: tier.color, textTransform: 'uppercase' }}>
          {tier.label}
        </div>
        <div style={{ width: 32, height: 1, background: 'var(--ajp-line)' }} />
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10.5, fontWeight: 500, color: 'var(--ajp-ink-dim)' }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: tier.color }} />
          via {j.platform}
        </div>
      </div>
    </div>
  );
}

/**
 * 24/7 Auto-Apply tab content. Server-side queue powered by Greenhouse,
 * Lever, Workable, and Ashby public APIs.
 *
 * Renders:
 *   - "JOBS ›" tab header (Recommended / Applied) + Refresh
 *   - JobRight-style filter bar (search, location, title, workplace, date, sort)
 *   - Skeleton loader while fetching
 *   - Empty state (varies by plan / active filters)
 *   - Job cards (when jobs are loaded)
 *
 * Pure presentation. Parent owns all queue state + handlers.
 */
export default function QueueTab({
  // Data
  usage,
  resetCountdown,
  queueJobs,
  queueCounts,
  queueLoading,
  queueRefreshing,
  queueSearching,
  onSearchJobs,

  // Settings
  queueMode,
  setQueueMode,
  queueThreshold,
  setQueueThreshold,
  queueStatusFilter,
  setQueueStatusFilter,
  queueSettingsSavedAt,

  // Handlers
  refreshQueue,
  togglePause,
  saveQueueSettings,
  decideJob,

  // Navigation (for empty-state CTAs)
  navigate,
  setActiveTab,

  apiBaseUrl,
}) {
  // ─── JobRight-style client-side filter bar ─────────────────────────────────
  // The status pills further up filter server-side by lifecycle status; these
  // refine the returned list in the browser. Location/title/platform/workplace
  // options are derived from the jobs present; seniority/job-type/years are
  // derived from each job's title (we don't store those columns).
  const [filters, setFilters] = useState({ location: [], title: [], platform: [], workplace: [], seniority: [], jobType: [] });
  const [datePosted, setDatePosted] = useState('any');   // 'any' | '1' | '7' | '30'
  const [yearsExp, setYearsExp] = useState(null);         // null = "open to all", else max years you have
  const [sortBy, setSortBy] = useState('match');          // 'match' | 'recent'
  const [searchQuery, setSearchQuery] = useState('');
  const [openFilter, setOpenFilter] = useState(null);     // id of the open dropdown, or null

  // Tab state lives here so "Liked" can be a client-side view (no server fetch).
  // 'waiting'/'applied' proxy to the parent (which fetches); 'liked' does not.
  const [tab, setTab] = useState('waiting');
  const selectTab = (key) => { setTab(key); if (key !== 'liked') setQueueStatusFilter(key); };
  useEffect(() => {
    if (queueStatusFilter !== 'liked' && queueStatusFilter !== tab) setTab(queueStatusFilter);
  }, [queueStatusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Liked jobs — stored client-side (localStorage) with the full job object so
  // the Liked tab renders instantly without a server round-trip.
  const LIKED_KEY = 'terview_liked_jobs';
  const [likedJobs, setLikedJobs] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LIKED_KEY)) || []; } catch { return []; }
  });
  const isLiked = (id) => likedJobs.some(x => x.id === id);
  const toggleLike = (job) => setLikedJobs(prev => {
    const next = prev.some(x => x.id === job.id) ? prev.filter(x => x.id !== job.id) : [{ ...job }, ...prev];
    try { localStorage.setItem(LIKED_KEY, JSON.stringify(next)); } catch { /* quota — ignore */ }
    return next;
  });

  const allJobs = tab === 'liked' ? likedJobs : (queueJobs || []);
  const uniq = (arr) => [...new Set(arr.filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b)));
  const workplaceOf = (loc) => {
    const s = String(loc || '').toLowerCase();
    if (/\bremote\b/.test(s)) return 'Remote';
    if (/\bhybrid\b/.test(s)) return 'Hybrid';
    return 'On-site';
  };
  const withinDays = (ts, days) => ts && (Date.now() - new Date(ts).getTime()) <= days * 864e5;

  const locationOpts = uniq(allJobs.map(j => j.location));
  const titleOpts = uniq(allJobs.map(j => j.title));
  const platformOpts = uniq(allJobs.map(j => j.platform));
  const workplaceOpts = uniq(allJobs.map(j => workplaceOf(j.location)));

  const setF = (key, vals) => setFilters(f => ({ ...f, [key]: vals }));
  const activeFilterCount =
    filters.location.length + filters.title.length + filters.platform.length +
    filters.workplace.length + filters.seniority.length + filters.jobType.length +
    (datePosted !== 'any' ? 1 : 0) + (yearsExp != null ? 1 : 0) + (searchQuery ? 1 : 0);
  const clearAllFilters = () => {
    setFilters({ location: [], title: [], platform: [], workplace: [], seniority: [], jobType: [] });
    setDatePosted('any'); setYearsExp(null); setSearchQuery('');
  };

  const filteredJobs = allJobs.filter(j => {
    if (filters.location.length && !filters.location.includes(j.location)) return false;
    if (filters.title.length && !filters.title.includes(j.title)) return false;
    if (filters.platform.length && !filters.platform.includes(j.platform)) return false;
    if (filters.workplace.length && !filters.workplace.includes(workplaceOf(j.location))) return false;
    if (filters.seniority.length && !filters.seniority.includes(seniorityOf(j.title))) return false;
    if (filters.jobType.length && !filters.jobType.includes(jobTypeOf(j.title))) return false;
    if (yearsExp != null && (yearsNum[seniorityOf(j.title)] ?? 3) > yearsExp) return false;
    if (datePosted !== 'any' && !withinDays(j.discovered_at, Number(datePosted))) return false;
    if (searchQuery.trim()) {
      // Word-based, punctuation-insensitive: every word in the query must
      // appear somewhere in title/company/location/description. So ".net" and
      // "net" both match ".NET Developer", and "developer remote" needs both.
      const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ');
      const hay = norm(`${j.title} ${j.company} ${j.location} ${j.jd_excerpt || ''}`);
      const words = norm(searchQuery).split(' ').filter(Boolean);
      if (!words.every(w => hay.includes(w))) return false;
    }
    return true;
  }).sort((a, b) => sortBy === 'recent'
    ? new Date(b.discovered_at || 0) - new Date(a.discovered_at || 0)
    : (Number(b.match_score) || 0) - (Number(a.match_score) || 0));

  // Infinite scroll: render a growing slice of the filtered set so the DOM
  // stays light. `visibleCount` grows by PAGE each time the user nears the
  // bottom (see onScroll on the list). Filtering/sorting still run over
  // everything. Reset back to the first page whenever the filters change.
  const PAGE = 30;
  const [visibleCount, setVisibleCount] = useState(PAGE);
  useEffect(() => {
    setVisibleCount(PAGE);
  }, [searchQuery, JSON.stringify(filters), datePosted, yearsExp, sortBy, tab]);
  const visibleJobs = filteredJobs.slice(0, visibleCount);
  const onListScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 500) {
      setVisibleCount((c) => (c < filteredJobs.length ? Math.min(c + PAGE, filteredJobs.length) : c));
    }
  };

  // ── Dropdown-pill styling + render helpers (dark theme, JobRight geometry).
  //    Defined as plain functions — NOT components — so they don't remount the
  //    open dropdown on every parent render. Open/close state is lifted to
  //    `openFilter` so only one menu is open at a time.
  const pillStyle = (active, open) => ({
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 999,
    fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', lineHeight: 1.2,
    background: active ? 'var(--ajp-ok-wash)' : 'var(--ajp-fill-2)',
    color: active ? 'var(--ajp-ok)' : 'var(--ajp-ink-2)',
    border: `1px solid ${open ? 'var(--ajp-ok-fill)' : active ? 'var(--ajp-ok-line)' : 'var(--ajp-line)'}`,
    transition: 'background .15s, border-color .15s, color .15s',
  });
  const caret = (open) => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
      strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s', opacity: 0.7 }}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
  const menuStyle = (align) => ({
    position: 'absolute', top: 'calc(100% + 7px)', zIndex: 55,
    left: align === 'right' ? 'auto' : 0, right: align === 'right' ? 0 : 'auto',
    minWidth: 214, maxWidth: 320,
    background: 'var(--ajp-menu)', border: '1px solid var(--ajp-line)', borderRadius: 14,
    boxShadow: 'var(--ajp-menu-shadow)', padding: 6,
  });

  // Multi-select pill (Location, Job title, Platform, Workplace).
  const multiPill = ({ id, label, options, selected, onChange }) => {
    const open = openFilter === id;
    const display = selected.length === 0 ? label
      : selected.length === 1 ? selected[0]
      : `${selected[0]} +${selected.length - 1}`;
    return (
      <div key={id} style={{ position: 'relative' }}>
        <button onClick={() => setOpenFilter(open ? null : id)} style={pillStyle(selected.length > 0, open)}>
          <span style={{ maxWidth: 168, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{display}</span>
          {caret(open)}
        </button>
        {open && (
          <div style={menuStyle()}>
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              {options.length === 0 && (
                <div style={{ padding: '10px 12px', fontSize: 12.5, color: 'var(--ajp-ink-muted)' }}>No options yet</div>
              )}
              {options.map(opt => {
                const on = selected.includes(opt);
                return (
                  <div key={opt} onClick={() => onChange(on ? selected.filter(x => x !== opt) : [...selected, opt])}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 9,
                      fontSize: 13, cursor: 'pointer', color: on ? 'var(--ajp-ink)' : 'var(--ajp-ink-2)',
                      background: on ? 'var(--ajp-ok-wash)' : 'transparent' }}>
                    <span style={{ width: 16, height: 16, borderRadius: 5, flexShrink: 0, display: 'flex',
                      alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800,
                      border: `1px solid ${on ? 'var(--ajp-ok-fill)' : 'var(--ajp-line)'}`,
                      background: on ? 'var(--ajp-ok-fill)' : 'transparent', color: 'var(--ajp-ok-fill-ink)' }}>{on ? '✓' : ''}</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt}</span>
                  </div>
                );
              })}
            </div>
            {selected.length > 0 && (
              <div onClick={() => onChange([])}
                style={{ padding: '8px 10px', marginTop: 4, borderTop: '1px solid var(--ajp-line)',
                  fontSize: 12.5, color: 'var(--ajp-danger)', cursor: 'pointer', fontWeight: 600 }}>Clear</div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Single-select pill (Date posted, Sort).
  const selectPill = ({ id, label, options, value, onChange, defaultValue, align }) => {
    const open = openFilter === id;
    const cur = options.find(o => o.v === value);
    const active = value !== defaultValue;
    return (
      <div key={id} style={{ position: 'relative' }}>
        <button onClick={() => setOpenFilter(open ? null : id)} style={pillStyle(active, open)}>
          <span>{active && cur ? cur.label : label}</span>
          {caret(open)}
        </button>
        {open && (
          <div style={{ ...menuStyle(align), minWidth: 190 }}>
            {options.map(o => {
              const on = o.v === value;
              return (
                <div key={o.v} onClick={() => { onChange(o.v); setOpenFilter(null); }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                    padding: '9px 10px', borderRadius: 9, fontSize: 13, cursor: 'pointer',
                    color: on ? 'var(--ajp-ok)' : 'var(--ajp-ink-2)',
                    background: on ? 'var(--ajp-ok-wash)' : 'transparent' }}>
                  <span>{o.label}</span>{on && <span>✓</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Years-of-experience slider pill (0–11) — JobRight's "Required Experience".
  // Filters to jobs whose implied required years ≤ the value you have.
  const yearsPill = () => {
    const open = openFilter === 'years';
    const active = yearsExp != null;
    return (
      <div key="years" style={{ position: 'relative' }}>
        <button onClick={() => setOpenFilter(open ? null : 'years')} style={pillStyle(active, open)}>
          <span>{active ? `${yearsExp}${yearsExp >= 11 ? '+' : ''} yrs exp` : 'Years of Experience'}</span>
          {caret(open)}
        </button>
        {open && (
          <div style={{ ...menuStyle(), minWidth: 272, padding: '14px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ajp-ink)' }}>Required Experience</span>
              <button onClick={() => setYearsExp(active ? null : 0)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'none', border: 'none',
                         cursor: 'pointer', fontSize: 12, color: !active ? 'var(--ajp-ok)' : 'var(--ajp-ink-dim)' }}>
                Open to all
                <span style={{ width: 34, height: 18, borderRadius: 999, padding: 2, display: 'inline-flex',
                               transition: 'background .15s', background: !active ? 'var(--ajp-ok-fill)' : 'var(--ajp-track)',
                               justifyContent: !active ? 'flex-end' : 'flex-start' }}>
                  <span style={{ width: 14, height: 14, borderRadius: '50%', background: '#fff' }} />
                </span>
              </button>
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: active ? 'var(--ajp-ok)' : 'var(--ajp-ink-muted)', marginBottom: 8 }}>
              {active ? `${yearsExp}${yearsExp >= 11 ? '+' : ''} Year${yearsExp === 1 ? '' : 's'}` : 'Open to all levels'}
            </div>
            <input type="range" min={0} max={11} step={1} value={yearsExp ?? 0}
              onChange={(e) => setYearsExp(Number(e.target.value))}
              style={{ width: '100%', accentColor: 'var(--ajp-ok-fill)', cursor: 'pointer' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--ajp-ink-muted)', marginTop: 4 }}>
              <span>0</span><span>11+</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div key="tab-queue" className="ajp-tab-pane">

      {/* Section header removed — page already has the QuickApply / Auto Apply
          pill switcher at the top, which doubles as the section identifier. */}

      {/* ─── JOBS (the queue) ───
          Single full-width column: status tabs + JobRight-style filter bar
          on top, jobs list scrolling internally below. */}
      <div className="ajp-queue-two-col" style={{ marginTop: 16, display: 'flex', gap: 16, alignItems: 'stretch' }}>

        <div className="ajp-queue-right">

        {/* ─── JobRight-style tab header: "JOBS ›" + text tabs with count
              badges. Same six lifecycle statuses as before (they're real and
              functional — Failed/Rejected matter), just restyled as tabs. ─── */}
        <div style={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 20, rowGap: 8,
          flexShrink: 0, borderBottom: '1px solid var(--ajp-line)',
        }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 9, marginRight: 4, paddingBottom: 8,
            fontWeight: 800, fontSize: 19, letterSpacing: '-0.02em', color: 'var(--ajp-ink)',
          }}>
            JOBS <span style={{ color: 'var(--ajp-ink-muted)', fontWeight: 600, fontSize: 18 }}>›</span>
          </span>
          {[
            { key: 'waiting', label: 'All Jobs', count: queueCounts?.waiting ?? 0 },
            { key: 'applied', label: 'Applied',  count: queueCounts?.applied ?? 0 },
            { key: 'liked',   label: 'Liked',    count: likedJobs.length },
          ].map(({ key, label, count }) => {
            const active = tab === key;
            return (
              <button
                key={key || 'all'}
                onClick={() => selectTab(key)}
                style={{
                  position: 'relative', marginBottom: -1, padding: '4px 1px 9px', background: 'none',
                  border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8,
                  fontSize: 15, fontWeight: active ? 800 : 600, letterSpacing: '-0.01em',
                  color: active ? 'var(--ajp-ink)' : 'var(--ajp-ink-dim)',
                  borderBottom: `2.5px solid ${active ? 'var(--ajp-brand)' : 'transparent'}`,
                  transition: 'color 0.15s',
                }}>
                {label}
                <span style={{
                  minWidth: 20, height: 20, padding: '0 6px', borderRadius: 999,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 800,
                  background: active ? 'var(--ajp-brand)' : 'var(--ajp-fill-2)',
                  color: active ? '#fff' : 'var(--ajp-ink-dim)',
                }}>
                  {count}
                </span>
              </button>
            );
          })}

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', paddingBottom: 6 }}>
            <button
              onClick={refreshQueue}
              disabled={queueRefreshing}
              style={{
                padding: '8px 16px', borderRadius: 999, fontSize: 13, fontWeight: 600,
                background: queueRefreshing ? '#555' : '#7c3aed',
                color: '#fff', border: 'none',
                cursor: queueRefreshing ? 'wait' : 'pointer',
              }}>
              {queueRefreshing ? '🔄 Discovering…' : '🔄 Refresh'}
            </button>
          </div>
        </div>

        {/* ─── JobRight-style filter bar (client-side refine + sort) ─── */}
        <div style={{ position: 'relative', zIndex: 30 }}>
          {/* Full-viewport catcher: click anywhere outside a menu to close it. */}
          {openFilter && (
            <div onClick={() => setOpenFilter(null)} style={{ position: 'fixed', inset: 0, zIndex: 1 }} />
          )}
          <div style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
            {/* Search — filters the list AND (on Enter) pulls fresh matching
                jobs from Google Jobs, adding them to the queue. */}
            <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              <span style={{ position: 'absolute', left: 12, color: 'var(--ajp-ink-muted)', display: 'flex', pointerEvents: 'none' }}>
                {queueSearching ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round" style={{ animation: 'ajpSpin 0.7s linear infinite' }}>
                    <path d="M21 12a9 9 0 1 1-6.2-8.5" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
                )}
              </span>
              <input value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !queueSearching && searchQuery.trim()) onSearchJobs?.(searchQuery.trim()); }}
                disabled={queueSearching}
                placeholder={queueSearching ? 'Searching the web…' : 'Search jobs — press Enter to find more'}
                title="Type a role and press Enter to pull fresh matching jobs from the web"
                style={{ padding: '6px 12px 6px 30px', borderRadius: 999, fontSize: 12,
                  background: 'var(--ajp-fill-2)', border: '1px solid var(--ajp-line)',
                  color: 'var(--ajp-ink)', outline: 'none', width: 210, opacity: queueSearching ? 0.7 : 1 }} />
            </div>

            {multiPill({ id: 'location', label: 'Location', options: locationOpts, selected: filters.location, onChange: v => setF('location', v) })}
            {multiPill({ id: 'title', label: 'Job title', options: titleOpts, selected: filters.title, onChange: v => setF('title', v) })}
            {multiPill({ id: 'seniority', label: 'Experience Level', options: SENIORITY_LEVELS, selected: filters.seniority, onChange: v => setF('seniority', v) })}
            {multiPill({ id: 'jobType', label: 'Job type', options: JOB_TYPES, selected: filters.jobType, onChange: v => setF('jobType', v) })}
            {multiPill({ id: 'workplace', label: 'Workplace', options: workplaceOpts, selected: filters.workplace, onChange: v => setF('workplace', v) })}
            {selectPill({ id: 'date', label: 'Date posted', value: datePosted, onChange: setDatePosted, defaultValue: 'any',
              options: [{ v: 'any', label: 'Any time' }, { v: '1', label: 'Past 24 hours' }, { v: '7', label: 'Past week' }, { v: '30', label: 'Past month' }] })}
            {yearsPill()}
            {multiPill({ id: 'platform', label: 'Platform', options: platformOpts, selected: filters.platform, onChange: v => setF('platform', v) })}

            {/* Clear all — only when a filter is active */}
            {activeFilterCount > 0 && (
              <button onClick={clearAllFilters}
                style={{ ...pillStyle(false, false), background: 'var(--ajp-danger-wash)',
                  border: '1px solid var(--ajp-danger-line)', color: 'var(--ajp-danger-soft)' }}>
                ✕ Clear all ({activeFilterCount})
              </button>
            )}

            {/* Sort — right-aligned, always shows current sort like JobRight's "Top Matched" */}
            <div style={{ marginLeft: 'auto' }}>
              {selectPill({ id: 'sort', label: 'Sort', value: sortBy, onChange: setSortBy, defaultValue: '__never__', align: 'right',
                options: [{ v: 'match', label: 'Top Matched' }, { v: 'recent', label: 'Most Recent' }] })}
            </div>
          </div>
        </div>

        {/* Skeleton loader during fetch */}
        {queueLoading && (
          <div className="ajp-skel-table">
            {[0, 1, 2, 3, 4].map(i => (
              <div key={i} className="ajp-skel-row">
                <div className="ajp-skeleton ajp-skel-cell tall" />
                <div className="ajp-skeleton ajp-skel-cell" />
                <div className="ajp-skeleton ajp-skel-cell" />
                <div className="ajp-skeleton ajp-skel-cell short" />
                <div className="ajp-skeleton ajp-skel-cell short" />
                <div className="ajp-skeleton ajp-skel-cell short" />
                <div className="ajp-skeleton ajp-skel-cell short" />
                <div className="ajp-skeleton ajp-skel-cell short" />
              </div>
            ))}
          </div>
        )}

        {/* Free-plan empty state — different copy than the per-status states */}
        {!queueLoading && tab !== 'liked' && usage?.plan === 'free' && allJobs.length === 0 && (
          <div className="ajp-empty">
            <div className="ajp-empty-icon">🔒</div>
            <div className="ajp-empty-title">24/7 Auto-Apply isn't on your plan.</div>
            <div className="ajp-empty-sub">
              You're on the <strong>Free</strong> plan. Live Auto-Apply (LinkedIn / Dice / etc.)
              works fully — but background applies via Greenhouse · Lever · Workable · Ashby
              need a paid plan. <strong>Lite ($29.99/mo)</strong> unlocks 5 per day.
            </div>
            <div className="ajp-empty-actions">
              <button className="ajp-empty-btn" onClick={() => window.dispatchEvent(new CustomEvent('openPricingModal'))}>
                ✨ See plans
              </button>
              <button className="ajp-empty-btn secondary" onClick={() => setActiveTab('live')}>
                Use Live Auto-Apply instead
              </button>
            </div>
          </div>
        )}

        {/* Per-status empty states — only shown to paid users */}
        {!queueLoading && usage?.plan !== 'free' && allJobs.length === 0 && (() => {
          const filter = tab || 'waiting';
          const config = {
            waiting:  {
              icon: '🔍',
              title: 'No jobs to review yet.',
              sub: 'Click Refresh to discover jobs across Greenhouse, Lever, Workable, Ashby, and Workday.',
              primary: { label: '🔄 Refresh', onClick: refreshQueue, disabled: queueRefreshing },
              secondary: null,
            },
            applied:  {
              icon: '🎯',
              title: 'No applications yet.',
              sub: 'Jobs you apply to with Autofill will show up here.',
              primary: { label: '← Back to all jobs', onClick: () => selectTab('waiting') },
            },
            liked:    {
              icon: '🤍',
              title: 'No liked jobs yet.',
              sub: 'Tap the heart on any job to save it here.',
              primary: { label: '← Back to all jobs', onClick: () => selectTab('waiting') },
            },
          };
          const c = config[filter] || config.waiting;
          return (
            <div className="ajp-empty">
              <div className="ajp-empty-icon">{c.icon}</div>
              <div className="ajp-empty-title">{c.title}</div>
              <div className="ajp-empty-sub">{c.sub}</div>
              <div className="ajp-empty-actions">
                {c.primary && (
                  <button className="ajp-empty-btn" onClick={c.primary.onClick} disabled={c.primary.disabled}>
                    {c.primary.label}
                  </button>
                )}
                {c.secondary && (
                  <button className="ajp-empty-btn secondary" onClick={c.secondary.onClick}>
                    {c.secondary.label}
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {/* Server returned jobs, but the active filters hide them all. */}
        {!queueLoading && allJobs.length > 0 && filteredJobs.length === 0 && (
          <div className="ajp-empty">
            <div className="ajp-empty-icon">🔍</div>
            <div className="ajp-empty-title">No jobs match these filters.</div>
            <div className="ajp-empty-sub">Try widening a filter or clearing them all.</div>
            <div className="ajp-empty-actions">
              <button className="ajp-empty-btn" onClick={clearAllFilters}>✕ Clear all filters</button>
            </div>
          </div>
        )}

        {/* Jobs — infinite-scroll list inside the left column. */}
        {filteredJobs.length > 0 && (
          <div className="ajp-queue-table-wrap" onScroll={onListScroll}>
            {visibleJobs.map(j => (
              <JobCard key={j.id} j={j} usage={usage} decideJob={decideJob} apiBaseUrl={apiBaseUrl}
                liked={isLiked(j.id)} onToggleLike={() => toggleLike(j)} />
            ))}
            <div style={{ textAlign: 'center', padding: '14px 0 8px', fontSize: 12.5, color: 'var(--ajp-ink-muted)' }}>
              {visibleCount < filteredJobs.length
                ? `Loading more… (${visibleCount} of ${filteredJobs.length})`
                : `That's all — ${filteredJobs.length} job${filteredJobs.length === 1 ? '' : 's'}`}
            </div>
          </div>
        )}
        </div>{/* /ajp-queue-right */}

        {/* Separate preview box — its own div on the right, sibling of the
            jobs container (job preview will render here later). */}
        <div className="ajp-queue-preview">
          <div style={{ padding: 24 }}>
            <div style={{ fontSize: 30, marginBottom: 10, opacity: 0.5 }}>🗂️</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ajp-ink-dim)' }}>Select a job</div>
            <div style={{ fontSize: 12.5, marginTop: 4, color: 'var(--ajp-ink-muted)' }}>Its details will show here.</div>
          </div>
        </div>
      </div>
    </div>
  );
}